/** Content-free identities survive clearing an unauthorized preview body. */
export function previewOwnershipRecord(preview, denied = false) {
  const ownership = { denied };
  for (const key of ['preview_id', 'chat_uuid', 'response_id', 'response_message_id', 'turn_id', 'run_id']) {
    if (preview?.[key]) ownership[key] = String(preview[key]);
  }
  const version = Number(preview?.committed?.version);
  if (preview?.committed?.artifact_id && Number.isInteger(version) && version > 0) {
    ownership.committed = { artifact_id: String(preview.committed.artifact_id), version };
  }
  return ownership;
}

/** Ephemeral, per-activation document projections. Never attach this state to sessions. */
export function createDocumentPreviewController({ fetchPreview, onChange = () => {}, now = Date.now,
  schedule = setTimeout, unschedule = clearTimeout } = {}) {
  // Finite aggregate diagnostics contain no document, chat, viewer, or content data.
  const metrics = Object.fromEntries(['notification_to_fetch_ms', 'preview_to_paint_ms', 'render_stall_ms', 'recovery_success', 'recovery_failure']
    .map(name => [name, { count: 0, total: 0, max: 0 }]));
  function recordMetric(name, value = 1) {
    const metric = metrics[name];
    if (!metric || !Number.isFinite(value) || value < 0) return;
    metric.count = Math.min(metric.count + 1, 1000000);
    metric.total = Math.min(metric.total + value, 1000000000);
    metric.max = Math.min(Math.max(metric.max, value), 1000000000);
  }
  let epoch = 0;
  let chatUuid = '';
  const entries = new Map();
  const emit = () => onChange(
    [...entries.values()].map(entry => entry.value).filter(Boolean),
    [...entries.values()].map(entry => entry.ownership).filter(Boolean),
  );
  const active = (entry, generation) => generation === epoch && entries.get(entry.id) === entry;
  function activate(uuid) {
    epoch += 1;
    for (const entry of entries.values()) {
      if (entry.timer !== null) unschedule(entry.timer);
      entry.abort?.abort();
    }
    entries.clear();
    chatUuid = String(uuid || '');
    emit();
    return epoch;
  }
  function queue(entry) {
    entry.dirty = true;
    if (entry.flight || entry.timer !== null) return;
    const delay = Math.max(0, entry.lastStarted + 500 - now(), entry.retryAt - now());
    entry.timer = schedule(() => { entry.timer = null; run(entry); }, delay);
  }
  function apply(entry, payload) {
    let current = entry.value;
    const snapshot = payload.snapshot || (payload.preview_id ? payload : null);
    // Full snapshots are authoritative recovery points; stale ones cannot roll back content.
    const snapshotMatches = snapshot && snapshot.preview_id === entry.id && (!snapshot.chat_uuid || String(snapshot.chat_uuid) === chatUuid);
    if (snapshot && !snapshotMatches) return;
    // A denial clears the content, not the last accepted revision. A stale
    // recovery snapshot must not restore an older protected body afterward.
    if (!current && snapshotMatches && (Number(snapshot.sequence) < entry.sequence
      || Number(snapshot.candidate_revision) < entry.candidateRevision)) return;
    if (!current && entry.ownership?.committed && !snapshot?.committed) return;
    if (snapshotMatches && (payload.reset !== false || typeof snapshot.content === 'string')) {
      if (!current || (Number(snapshot.sequence) > Number(current.sequence) && Number(snapshot.candidate_revision) >= Number(current.candidate_revision))) {
        if (!current?.committed || snapshot.committed) current = { ...snapshot };
      }
    }
    for (const update of payload.updates || []) {
      if (current && Number(update.sequence) <= Number(current.sequence)) continue;
      if (!current || Number(update.sequence) !== Number(current.sequence) + 1) {
        entry.reset = true; entry.dirty = true; break;
      }
      if (current.committed && !update.committed) continue;
      const append = update.kind === 'append' || update.type === 'append';
      if (append && Number(update.candidate_revision) !== Number(current.candidate_revision)) {
        entry.reset = true; entry.dirty = true; break;
      }
      current = { ...current, ...update, ...(append ? { content: current.content + String(update.content ?? update.append ?? '') } : {}) };
    }
    if (snapshotMatches && payload.reset === false && current
      && Number(snapshot.sequence) === Number(current.sequence)
      && (!current.committed || snapshot.committed)) {
      current = { ...current, ...snapshot };
    }
    if (snapshotMatches && payload.reset === false && (!current?.committed || snapshot.committed)
      && (!current || Number(snapshot.sequence) > Number(current.sequence))) {
      entry.reset = true;
      entry.dirty = true;
    }
    if (current) {
      entry.value = { ...current, operation_id: current.operation_id || entry.id, disclosure_paused: false, unavailable: false, retry_exhausted: false };
      entry.sequence = Number(current.sequence);
      entry.candidateRevision = Number(current.candidate_revision);
      entry.ownership = previewOwnershipRecord({ ...entry.ownership, ...current, chat_uuid: chatUuid });
      entry.retries = 0;
      entry.retryAt = 0;
      emit();
    }
  }
  function pauseForRetry(entry) {
    const retry = entry.retries < 5;
    if (retry) {
      entry.retries += 1;
      entry.retryAt = now() + Math.min(500 * 2 ** (entry.retries - 1), 8000);
      entry.dirty = true;
    }
    if (entry.value) {
      entry.value = { ...entry.value, disclosure_paused: true, retry_exhausted: !retry };
      emit();
    }
  }
  async function run(entry) {
    const generation = epoch;
    if (!active(entry, generation) || !chatUuid) return;
    entry.dirty = false;
    entry.lastStarted = now();
    if (entry.notifiedAt !== null) recordMetric('notification_to_fetch_ms', entry.lastStarted - entry.notifiedAt);
    entry.notifiedAt = null;
    entry.flight = true;
    const abort = new AbortController();
    entry.abort = abort;
    const cursor = entry.reset ? null : entry.value;
    entry.reset = false;
    try {
      const payload = await fetchPreview(entry.id, {
        chatUuid, sequence: cursor?.sequence, candidateRevision: cursor?.candidate_revision,
        signal: abort.signal,
      });
      if (!active(entry, generation) || abort.signal.aborted) return;
      if (payload.available === false) recordMetric('recovery_failure');
      if (payload.clear_preview) {
        entry.value = null;
        if (entry.ownership) entry.ownership = { ...entry.ownership, denied: true };
        entry.reset = true;
        entry.retryAt = 0;
        emit();
      } else if (payload.available === false) {
        if (payload.retryable === true) pauseForRetry(entry);
        else if (entry.value) {
          entry.value = { ...entry.value, disclosure_paused: false, unavailable: true, retry_exhausted: false };
          emit();
        }
      } else {
        apply(entry, payload);
        if (payload.reset) recordMetric('recovery_success');
      }
    } catch (error) {
      if (active(entry, generation)) recordMetric('recovery_failure');
      if (active(entry, generation) && error?.name !== 'AbortError') pauseForRetry(entry);
    } finally {
      if (active(entry, generation)) {
        entry.flight = false;
        entry.abort = null;
        if (entry.dirty) queue(entry);
      }
    }
  }
  function notify(reference) {
    if (!chatUuid || !reference || (reference.chat_uuid && String(reference.chat_uuid) !== chatUuid)) return;
    const id = String(reference.preview_id || '');
    if (!id || id.length > 200) return;
    let entry = entries.get(id);
    if (!entry) {
      entry = { id, value: null, ownership: null, sequence: -1, candidateRevision: -1,
        lastStarted: -Infinity, timer: null, flight: false, dirty: false, reset: false, abort: null, notifiedAt: null, retries: 0, retryAt: 0 };
      entries.set(id, entry);
    }
    // Discovery is chat-authorized and contains identities only. Retain its
    // owner before the first content fetch, including when an ID-only realtime
    // notification created the entry before the fuller discovery arrived.
    const referenceOwner = previewOwnershipRecord({ ...reference, chat_uuid: chatUuid });
    const committedChanged = referenceOwner.committed && (
      referenceOwner.committed.artifact_id !== entry.value?.committed?.artifact_id
      || referenceOwner.committed.version !== Number(entry.value?.committed?.version)
    );
    entry.ownership = {
      ...entry.ownership, ...referenceOwner,
      denied: entry.ownership?.denied === true,
      ...(entry.ownership?.pending || !entry.value || committedChanged ? { pending: true } : {}),
    };
    emit();
    if (entry.notifiedAt === null) entry.notifiedAt = now();
    queue(entry);
  }
  function discover(payload, expectedChat = chatUuid) {
    if (String(expectedChat || '') !== chatUuid) return;
    const discovery = payload?.document_previews;
    if (!discovery || discovery.enabled !== true) return;
    for (const reference of discovery.references || []) {
      const entry = entries.get(String(reference?.preview_id || ''));
      const sequence = Number(reference?.sequence);
      if (Number.isInteger(sequence) && sequence >= 0 && entry?.value
        && sequence <= Number(entry.value.sequence) && !entry.reset
        && !entry.value.disclosure_paused && !entry.value.unavailable) continue;
      notify(reference);
    }
  }
  function retry(previewId) {
    const entry = entries.get(String(previewId || ''));
    if (!entry) return;
    entry.retries = 0;
    entry.retryAt = 0;
    if (entry.timer !== null) { unschedule(entry.timer); entry.timer = null; }
    entry.abort?.abort();
    queue(entry);
  }
  return { activate, notify, discover, retry, recordMetric, getMetrics() { return JSON.parse(JSON.stringify(metrics)); }, reconnect() {
    for (const entry of entries.values()) {
      retry(entry.id);
    }
  },
    dispose() { activate(''); }, get epoch() { return epoch; }, get chatUuid() { return chatUuid; } };
}

export async function fetchDocumentPreview(previewId, { chatUuid, sequence, candidateRevision, signal }) {
  const query = new URLSearchParams({ chat_uuid: chatUuid });
  if (sequence !== undefined) query.set('sequence', String(sequence));
  if (candidateRevision !== undefined) query.set('candidate_revision', String(candidateRevision));
  const response = await fetch(`/chat/document-previews/${encodeURIComponent(previewId)}?${query}`, {
    credentials: 'same-origin', cache: 'no-store', signal,
  });
  if (response.status === 403) return { available: false, clear_preview: true };
  if (response.status === 404 || response.status === 410) return { available: false };
  if (!response.ok) return { available: false, retryable: true };
  // A terminal notification may be the final wake-up for this preview. Reject
  // truncated JSON (or an empty success body) so the controller retries it.
  const payload = await response.json();
  const snapshot = payload?.snapshot ?? payload;
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)
    || (payload.available !== false && (!snapshot || typeof snapshot !== 'object'
      || Array.isArray(snapshot) || typeof snapshot.preview_id !== 'string' || !snapshot.preview_id))) {
    throw new Error('Invalid document preview response');
  }
  return payload;
}
